// Storage helpers (issue #143 sub-step 4): the v5 SDK's
// `Storage.get(key, { expires })` auto-prefixed `public/` based on
// `level: "public"` and returned a string. v6's
// `getUrl({ path, options: { expiresIn } })` does NEITHER -- the
// caller passes the literal storage path including the access prefix
// (so we prepend `public/` here, matching `amplify/storage/resource.ts`'s
// `"public/*"` rule and operator answer Q5 of #143), and the call
// returns `{ url: URL, expiresAt: Date }`. Existing call sites of
// `getPresignedUrl` consume a string (passed to `fetch`, `<img src>`,
// or `setState`), so this helper unwraps the URL to its `.toString()`
// representation to keep call sites unchanged.
import { getUrl } from 'aws-amplify/storage';
import { recommendationFileTypes } from './recommendationFileTypes';
import { Recommendee, RecommendationFileType, RecommendationTarget, Lang } from './types';

export const naWrapper = (value: string, naValue?: string) => value === 'NA' || value === 'nan' ? naValue ?? '' : value;

// Group-based access check. Callers compute the boolean from the
// session's `cognito:groups` claim via `auth-groups.ts` predicates and
// pass it in -- this helper stays single-purpose (the
// applicant-relationship match) and doesn't itself reach for a session.
//
// Pre-MR-!42 review note: this helper used to be called with an
// `isAdminOrGraderUser` derived from `isAdminOrGrader(username)`, which
// hardcoded the legacy Gen 1 username literals (`admin-7w3bh8xp2ld4x` /
// `grader-r04w6p32dfvfn`). Those username-keyed helpers have been
// removed (the only correct source is the JWT's groups claim). Callers
// now pass the result of `isAdminOrGraderByGroups(groups)`.
//
// MR !42 review thread `859dcb10...`: short-circuit to `false` when
// `applicant` is null/undefined. v5's call sites also crashed on null
// access, but the v6 `client.models.ProdApplicant.get(...)` shape can
// legitimately return `{ data: null }` for non-existent applicants
// (deleted, wrong cycleYear, race with ingestion) AND for non-numeric
// `applicantNum` inputs (`parseInt("garbage", 10) === NaN`). Without
// this guard the bare `applicant.recommender1Email` access throws a
// TypeError that the caller's outer try/catch surfaces as
// access-denied -- misleading for the deep-link-with-stale-id case.
// Returning `false` here lets the caller distinguish (or not) and
// fail-closed.
export const canAccessApplicant = (applicant: any, userEmail: string, isAdminOrGraderUser: boolean): boolean => {
  if (isAdminOrGraderUser) {
    return true;
  }

  if (!applicant) {
    return false;
  }

  return (
    applicant.recommender1Email === userEmail ||
    applicant.recommender2Email === userEmail ||
    applicant.recommender3Email === userEmail ||
    applicant.schoolReportWriterEmail === userEmail
  );
};

export const getMissingDocuments = (data: Recommendee[]) => {
    const missingDocuments: RecommendationTarget[] = [];
    data.forEach(recommendee => {
        Object.keys(recommendee.documentsUploaded).forEach(key => {
            if (!(recommendee.documentsUploaded[key as RecommendationFileType]?.fileUrl)) missingDocuments.push({ recommendee, fileType: key as RecommendationFileType });
        });
    });
    return missingDocuments;
};

export const renderRecommendeeFullName = (recommendee: Recommendee, lang: Lang) => (
    lang === 'en' ? `${recommendee.englishName}` : `${recommendee.name}`
);

interface GetFileKeyParams {
    recommendee: {
        name: string;
        applicantNum: string;
    };
    fileType: RecommendationFileType;
}

export const getFileKey = (target: GetFileKeyParams) => {
    const { fileType, recommendee: { applicantNum, name } } = target;
    const fileKey = `${applicantNum} ${name}/${recommendationFileTypes[fileType].ja}.pdf`;
    return fileKey;
};

/**
 * v5 -> v6 path semantics (Q5 of #143):
 *
 *   v5: `Storage.get("foo.pdf", { expires: 120, level: "public" })`
 *       SDK auto-prefixed `public/` based on `level`; returned string.
 *   v6: `getUrl({ path: "public/foo.pdf", options: { expiresIn: 120 } })`
 *       Caller passes the literal access-prefixed path; returns
 *       `{ url: URL, expiresAt: Date }`.
 *
 * `key` here is the un-prefixed Gen 1 key shape
 * (`"{applicantNum} {name}/{type}.pdf"`). We prepend `public/` once,
 * inside the helper, so call sites stay identical to the v5 era and
 * the `public/*` storage access rule from `amplify/storage/resource.ts`
 * gets matched.
 */
export const getPresignedUrl = async (key: string): Promise<string> => {
    const { url } = await getUrl({
        path: `public/${key}`,
        options: { expiresIn: 120 },
    });
    return url.toString();
};

/**
 * Validate that a selected file is safe to upload to S3.
 *
 * Returns a localized error message when the file is empty (0 bytes),
 * or null when it's safe to upload. The helper assumes the caller has
 * already produced a real File object -- callers are responsible for
 * the "no file at all" case (cancel-the-picker, react-dropzone MIME
 * rejection, etc.) because each call site has a more specific story
 * to tell about why no file arrived (see issue #123 for the dropzone
 * MIME rejection case).
 *
 * The 0-byte case keeps showing up in real cycles (issue #82): some
 * submitters drop a file that turned out to be empty and only notice
 * after the deadline. Catching it client-side is the cheapest fix --
 * the recommender re-selects, no S3 round-trip.
 */
export const validateUploadFile = (file: File, lang: Lang): string | null => {
    if (file.size === 0) {
        return lang === 'en'
            ? 'This file is empty (0 bytes). Please re-export and try uploading again.'
            : 'このファイルは空です（0バイト）。ファイルを再保存してから再度アップロードしてください。';
    }
    return null;
};

export const matchRecommendee = (a: Recommendee, b: Recommendee) => a.applicantNum === b.applicantNum;

export const findRecommendeeIndex = (data: Recommendee[], recommendee: Recommendee) => (
    data.findIndex(r => matchRecommendee(r, recommendee))
);
